import { API_URL } from "../constants";
import { Event } from "../types";

export type HomeEvent = Event & {
  slug?: string;
  parentEventId?: string | null;
  isParent?: boolean;
  isPrivate?: boolean;
  isActive?: boolean;
};
export type HomeEventTimeFilter = "upcoming" | "next24Hours" | "after24Hours";
export type HomeEventFeedOptions = { limit?: number; excludeEventIds?: readonly string[] };

const PAGE_SIZE = 10;
const CACHE_TTL = 60_000;
const detailsCache = new Map<string, { expiresAt: number; promise: Promise<HomeEvent | null> }>();

function getEventDetails(id: string): Promise<HomeEvent | null> {
  const now = Date.now();
  detailsCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) detailsCache.delete(key);
  });
  const cached = detailsCache.get(id);
  if (cached) return cached.promise;

  // Both homepage sections share lookups, including requests already in flight.
  // Each caller can abandon its results without cancelling the other's request.
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  const promise = fetch(`${API_URL}/events/${encodeURIComponent(id)}`, { signal: controller.signal })
    .then(response => {
      if (response.status === 404 || response.status === 403) return null;
      if (!response.ok) throw new Error("Event details unavailable");
      return response.json() as Promise<HomeEvent>;
    })
    .catch(error => {
      detailsCache.delete(id);
      throw error;
    })
    .finally(() => window.clearTimeout(timeout));
  detailsCache.set(id, { expiresAt: now + CACHE_TTL, promise });
  return promise;
}

async function resolveRootEvent(candidate: HomeEvent): Promise<HomeEvent | null> {
  // Current production list responses omit hierarchy fields; details include them.
  const details = Object.prototype.hasOwnProperty.call(candidate, "parentEventId")
    ? candidate
    : await getEventDetails(candidate.id);
  if (!details) return null;
  const root = details.parentEventId
    ? await getEventDetails(details.parentEventId)
    : { ...candidate, ...details };
  if (!root || root.parentEventId || root.isPrivate || root.isActive === false) return null;
  return root;
}

export async function loadHomeEvents(country: string, timeFilter: HomeEventTimeFilter | undefined, signal: AbortSignal, options: HomeEventFeedOptions = {}): Promise<HomeEvent[]> {
  const results = new Map<string, HomeEvent>();
  const seenCandidates = new Set<string>();
  const limit = options.limit ?? PAGE_SIZE;
  const excludedIds = new Set(options.excludeEventIds);
  const now = Date.now();
  const cutoff = now + 24 * 60 * 60 * 1000;
  const next24Hours = timeFilter === "next24Hours";

  for (let page = 1; !signal.aborted && results.size < limit; page++) {
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE), country });
    if (next24Hours) {
      params.set("isSearch", "false");
      params.append("category[]", "All");
      params.set("startDate", new Date(now).toISOString());
      params.set("endDate", new Date(cutoff).toISOString());
    } else if (timeFilter) params.set("timeFilter", "upcoming");
    const endpoint = next24Hours ? "filter" : "home-events";
    const response = await fetch(`${API_URL}/events/${endpoint}?${params}`, { signal });
    if (!response.ok) throw new Error("Events unavailable");
    const data = await response.json();
    const items: HomeEvent[] = Array.isArray(data) ? data : data?.events;
    if (!Array.isArray(items)) throw new Error("Invalid event response");
    if (signal.aborted) return [];

    const unseen = items.filter(item => {
      if (seenCandidates.has(item.id)) return false;
      seenCandidates.add(item.id);
      return true;
    });
    if (!unseen.length) break;
    // The API rounds date ranges to calendar days. Enforce the rolling window
    // on actual occurrences before replacing a qualifying date with its parent.
    const candidates = next24Hours || timeFilter === "after24Hours" ? unseen.filter(item => {
      const start = new Date(item.startDate).getTime();
      return next24Hours ? start >= now && start <= cutoff : start > cutoff;
    }) : unseen;
    const roots = await Promise.all(candidates.map(resolveRootEvent));
    if (signal.aborted) return [];
    for (const root of roots) {
      if (root && !excludedIds.has(root.id) && !results.has(root.id)) results.set(root.id, root);
      if (results.size === limit) break;
    }
    // Refill slots after excluding duplicate dates and events shown above.
    if (items.length < PAGE_SIZE) break;
  }
  return Array.from(results.values());
}
